import { useState, useEffect, useContext } from "react";
import { useFormik } from "formik";
import { putWithResponse } from "../../../api/apis";
import { obtenerPosicionesLaboralesApi } from "../../../api/contratacion/datosLaboralesApi";
import { modalCargando, mensajeSimple, mensajeEstatico, mensajeConfirmacionModal, modalGuardandoCambiosModal, mensajeExitoModal } from "../../../components/ui/sweetAlerts";
import { DataContextContractual } from "../../../contextData/contextData";
import moment from "moment";

const cleanStr = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,;:_\-/]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const useModalEditarSolicitud = (solicitud, hideModal, onSuccess) => {
  const [posicionesLaborales, setPosicionesLaborales] = useState([]);
  const [cargando, setCargando] = useState(() => Boolean(solicitud && [1, 3].includes(Number(solicitud?.id_tipo_solicitud))));
  const { obtenerDiasAnticipoPorTipo, diasAnticipoGeneral, diasAnticipo, dataListado, setDataListado } = useContext(DataContextContractual);

  const getPosiciones = async () => {
    setCargando(true);
    try {
      const adminSapId = [3].includes(Number(solicitud?.id_tipo_solicitud))
        ? (solicitud?.cod_sap_administrador_destino || solicitud?.cod_sap_admin_destino || solicitud?.cod_sap_administrador || null)
        : (solicitud?.cod_sap_administrador || null);
      const resp = await obtenerPosicionesLaboralesApi(adminSapId);
      if (resp?.status === "success") {
        setPosicionesLaborales(resp.data);
      }
    } catch (err) {
      console.error("Error cargando posiciones:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (solicitud && [1, 3].includes(Number(solicitud?.id_tipo_solicitud))) {
      getPosiciones();
    } else {
      setCargando(false);
    }
  }, [solicitud]);

  useEffect(() => {
    if (
      posicionesLaborales &&
      Object.keys(posicionesLaborales).length > 0 &&
      solicitud?.cod_sap_nuevo_cargo
    ) {
      const currentVal = formik.values.cod_sap_nuevo_cargo || solicitud.cod_sap_nuevo_cargo;
      if (typeof currentVal === "string" && !currentVal.startsWith("{")) {
        let found = null;
        const normCode = (s) => String(s || "").trim().replace(/^0+/, "");
        const targetCodeNorm = normCode(currentVal);
        const targetDescClean = cleanStr(solicitud.nombre_nuevo_cargo);

        for (const grupo of Object.keys(posicionesLaborales)) {
          const list = posicionesLaborales[grupo];
          if (Array.isArray(list)) {
            found = list.find(
              (p) => normCode(p.MOES_COD_POSICION) === targetCodeNorm
            );
            if (found) break;

            if (targetDescClean) {
              found = list.find((p) => {
                const c = cleanStr(p.MOES_DESCRIPCION_POSICION);
                return c && (c === targetDescClean || c.includes(targetDescClean) || targetDescClean.includes(c));
              });
              if (found) break;
            }
          }
        }
        if (found) {
          formik.setFieldValue("cod_sap_nuevo_cargo", JSON.stringify(found));
        } else {
          formik.setFieldValue("cod_sap_nuevo_cargo", "");
          formik.setFieldValue("nueva_unidad_organizativa", "");
          formik.setFieldValue("descripcion_unidad_temporis", "");
          formik.setFieldValue("nuevo_plan_horario", "");
        }
      }
    }
  }, [posicionesLaborales, solicitud]);

  const validate = (values) => {
    const errObj = {};
    const tipo = Number(solicitud?.id_tipo_solicitud);

    if (!values.fecha_inicio_cambio) {
      errObj.fecha_inicio_cambio = "Requerido";
    }

    if ([1, 3].includes(tipo) && !values.cod_sap_nuevo_cargo) {
      errObj.cod_sap_nuevo_cargo = "Requerido";
    }

    if ([1, 2, 3].includes(tipo)) {
      if (!values.nueva_unidad_organizativa) errObj.nueva_unidad_organizativa = "Requerido";
      if (!values.nuevo_plan_horario) errObj.nuevo_plan_horario = "Requerido";
    }

    if ([4].includes(tipo)) {
      if (!values.tipo_cambio_contrato) errObj.tipo_cambio_contrato = "Requerido";
      if (["02"].includes(values.tipo_cambio_contrato) && !values.fecha_renovacion_contrato) {
        errObj.fecha_renovacion_contrato = "Requerido";
      }
    }

    if ([5].includes(tipo)) {
      if (!values.id_clausula_despido) errObj.id_clausula_despido = "Requerido";
      if (!values.motivo_desvinculacion) errObj.motivo_desvinculacion = "Requerido";
    }

    return errObj;
  };

  const submitForm = async (values) => {
    const confirmResult = await mensajeConfirmacionModal({
      titulo: "¿Confirmar actualización?",
      texto: `¿Está seguro de guardar las modificaciones a la solicitud #${solicitud?.id}?`,
      confirmButtonText: "Sí, guardar cambios",
      cancelButtonText: "Cancelar",
      mostrarIcono: false,
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    modalGuardandoCambiosModal("Guardando cambios en la solicitud...");
    try {
      const idTipo = Number(solicitud?.id_tipo_solicitud);
      const payload = {
        id_solicitud: solicitud.id,
        id_tipo_solicitud: idTipo,
        fecha_inicio_cambio: values.fecha_inicio_cambio,
        observacion_edicion: values.observacion_edicion || "",
      };

      if ([1, 3].includes(idTipo) && values.cod_sap_nuevo_cargo) {
        try {
          const parsedCargo = typeof values.cod_sap_nuevo_cargo === "string" && values.cod_sap_nuevo_cargo.startsWith("{")
            ? JSON.parse(values.cod_sap_nuevo_cargo)
            : { MOES_COD_POSICION: values.cod_sap_nuevo_cargo, MOES_DESCRIPCION_POSICION: values.nombre_nuevo_cargo };

          payload.cod_sap_nuevo_cargo = parsedCargo.MOES_COD_POSICION;
          payload.nombre_nuevo_cargo = parsedCargo.MOES_DESCRIPCION_POSICION;
          if (parsedCargo.MOES_COD_DIVISION) payload.moes_cod_division = parsedCargo.MOES_COD_DIVISION;
          if (parsedCargo.MOES_COD_SUB_DIVISION) payload.moes_cod_sub_division = parsedCargo.MOES_COD_SUB_DIVISION;
          if (parsedCargo.MOES_ID_GRUPO_DE_PERSONAL) payload.moes_id_grupo_de_personal = parsedCargo.MOES_ID_GRUPO_DE_PERSONAL;
          if (parsedCargo.MOES_ID_AREA_DE_PERSONAL) payload.moes_id_area_de_personal = parsedCargo.MOES_ID_AREA_DE_PERSONAL;
          if (parsedCargo.MOES_COD_FUNCION) payload.moes_cod_funcion = parsedCargo.MOES_COD_FUNCION;
          if (parsedCargo.MOES_COD_UNIDAD_ORGANIZATIVA) payload.moes_cod_unidad_organizativa = parsedCargo.MOES_COD_UNIDAD_ORGANIZATIVA;
          if (parsedCargo.MOES_ID_AREA_DE_NOMINA) payload.moes_id_area_de_nomina = parsedCargo.MOES_ID_AREA_DE_NOMINA;
        } catch (e) {
          payload.cod_sap_nuevo_cargo = values.cod_sap_nuevo_cargo;
        }
      }

      if ([1, 2, 3].includes(idTipo) && values.nueva_unidad_organizativa) {
        if (typeof values.nueva_unidad_organizativa === "object") {
          const uo = values.nueva_unidad_organizativa;
          payload.nueva_unidad_organizativa = uo.IdUnidadorganizativa || "";
          payload.localcodigoerp = uo.Localcodigoerp || "";
          payload.idunidadorganizativa = uo.IdUnidadorganizativa || "";
          payload.idcargogenerico = uo.IdCargogenerico || "";
          payload.idplanhorario = uo.IdPlanhorario || "";
          payload.descripcion_unidad_temporis = values.descripcion_unidad_temporis || `${uo.Unidadorganizativa} - ${uo.Cargogenericonombre}`;
        } else {
          payload.nueva_unidad_organizativa = values.nueva_unidad_organizativa;
        }

        if (values.descripcion_unidad_temporis) {
          payload.descripcion_unidad_temporis = values.descripcion_unidad_temporis;
        } else {
          const unitElem = document.querySelector("#nueva_unidad_organizativa .limpiarSelect__single-value");
          if (unitElem && unitElem.innerText) {
            payload.descripcion_unidad_temporis = unitElem.innerText;
          }
        }

        if (values.nuevo_plan_horario) {
          payload.nuevo_plan_horario = values.nuevo_plan_horario;
        }
      }

      if (idTipo === 3) {
        if (values.unidad_destino) payload.unidad_destino = values.unidad_destino;
        if (values.instalacion_administrador) {
          const { JEFE, ADMIN } = values.instalacion_administrador;
          if (JEFE) payload.cod_sap_jefe_unidad_destino = JEFE.REEO_CODIGO_SAP;
          if (ADMIN) payload.cod_sap_administrador_destino = ADMIN.REEO_CODIGO_SAP;
          const instElem = document.querySelector("#instalacion_administrador .limpiarSelect__single-value");
          if (instElem) payload.nombre_instalacion_destino = instElem.innerText;
        }
      }

      if (idTipo === 4) {
        if (values.tipo_cambio_contrato) payload.tipo_cambio_contrato = values.tipo_cambio_contrato;
        if (values.fecha_renovacion_contrato) payload.fecha_renovacion_contrato = values.fecha_renovacion_contrato;
      }

      if (idTipo === 5) {
        if (values.motivo_desvinculacion) payload.motivo_desvinculacion = values.motivo_desvinculacion;
        if (values.id_clausula_despido) payload.id_clausula_despido = values.id_clausula_despido;
      }

      // Generar automáticamente el log de observacion de edición
      const cambiosLog = [];

      if (payload.fecha_inicio_cambio && payload.fecha_inicio_cambio !== solicitud?.fecha_inicio_cambio) {
        cambiosLog.push(`Fecha efectiva: [${solicitud?.fecha_inicio_cambio || "N/A"}] -> [${payload.fecha_inicio_cambio}]`);
      }

      if (payload.cod_sap_nuevo_cargo && String(payload.cod_sap_nuevo_cargo) !== String(solicitud?.cod_sap_nuevo_cargo)) {
        const posAnt = solicitud?.cod_sap_nuevo_cargo
          ? `${solicitud.nombre_nuevo_cargo || ''} (${solicitud.cod_sap_nuevo_cargo})`.trim()
          : (solicitud?.nombre_nuevo_cargo || "N/A");
        const posNueva = payload.cod_sap_nuevo_cargo
          ? `${payload.nombre_nuevo_cargo || ''} (${payload.cod_sap_nuevo_cargo})`.trim()
          : (payload.nombre_nuevo_cargo || "N/A");
        cambiosLog.push(`Se modificó la posición de [${posAnt}] a [${posNueva}]`);
      }

      if (payload.descripcion_unidad_temporis && payload.descripcion_unidad_temporis !== solicitud?.descripcion_unidad_temporis) {
        const uoAnt = solicitud?.descripcion_unidad_temporis || solicitud?.nueva_unidad_organizativa || "N/A";
        cambiosLog.push(`Unidad Organizativa: [${uoAnt}] -> [${payload.descripcion_unidad_temporis}]`);
      }

      if (payload.nuevo_plan_horario && payload.nuevo_plan_horario !== solicitud?.nuevo_plan_horario) {
        cambiosLog.push(`Plan Horario: [${solicitud?.nuevo_plan_horario || "N/A"}] -> [${payload.nuevo_plan_horario}]`);
      }

      if (payload.nombre_instalacion_destino && payload.nombre_instalacion_destino !== solicitud?.nombre_instalacion_destino) {
        cambiosLog.push(`Instalación Destino: [${solicitud?.nombre_instalacion_destino || "N/A"}] -> [${payload.nombre_instalacion_destino}]`);
      }

      if (payload.tipo_cambio_contrato && payload.tipo_cambio_contrato !== solicitud?.tipo_cambio_contrato) {
        cambiosLog.push(`Tipo Cambio Contrato: [${solicitud?.tipo_cambio_contrato || "N/A"}] -> [${payload.tipo_cambio_contrato}]`);
      }

      if (payload.fecha_renovacion_contrato && payload.fecha_renovacion_contrato !== solicitud?.fecha_renovacion_contrato) {
        cambiosLog.push(`Fecha Renovación Contrato: [${solicitud?.fecha_renovacion_contrato || "N/A"}] -> [${payload.fecha_renovacion_contrato}]`);
      }

      if (payload.motivo_desvinculacion && payload.motivo_desvinculacion !== solicitud?.motivo_desvinculacion) {
        cambiosLog.push(`Motivo Desvinculación: [${solicitud?.motivo_desvinculacion || "N/A"}] -> [${payload.motivo_desvinculacion}]`);
      }

      if (payload.id_clausula_despido && String(payload.id_clausula_despido) !== String(solicitud?.id_clausula_despido)) {
        cambiosLog.push(`Cláusula Despido: [${solicitud?.id_clausula_despido || "N/A"}] -> [${payload.id_clausula_despido}]`);
      }

      payload.observacion_edicion = cambiosLog.length > 0
        ? `Valores modificados por RRHH: ${cambiosLog.join("; ")}`
        : "Valores modificados por RRHH.";

      const resp = await putWithResponse("cc/editar_solicitud", null, payload);

      if (resp?.status === 200 && resp?.data?.status === "success") {
        mensajeExitoModal({
          titulo: "¡Actualización exitosa!",
          texto: "",
          tiempo: 2200,
        });

        if (setDataListado && dataListado?.arrData) {
          setDataListado((prev) => ({
            ...prev,
            arrData: (prev.arrData || []).map((item) =>
              item.id === payload.id_solicitud
                ? {
                    ...item,
                    ...payload,
                    fecha_inicio_cambio: payload.fecha_inicio_cambio || item.fecha_inicio_cambio,
                    nombre_nuevo_cargo: payload.nombre_nuevo_cargo || item.nombre_nuevo_cargo,
                    descripcion_unidad_temporis: payload.descripcion_unidad_temporis || item.descripcion_unidad_temporis,
                    nuevo_plan_horario: payload.nuevo_plan_horario || item.nuevo_plan_horario,
                  }
                : item
            ),
          }));
        }

        if (hideModal) hideModal();
        if (onSuccess) onSuccess();
        window.dispatchEvent(new Event("refresh_solicitudes_table"));
      } else {
        const errorMsg = resp?.data?.message || "No se pudo actualizar la solicitud";
        mensajeEstatico("error", "Error al actualizar", errorMsg);
      }
    } catch (error) {
      console.error("Error editando solicitud:", error);
      mensajeEstatico("error", "Error inesperado", "Ocurrió un problema al procesar la edición.");
    }
  };

  const normalizeTipoCambio = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const s = String(val).trim().toLowerCase();
    if (s === "1" || s === "01" || s.includes("indefinido")) return "01";
    if (s === "2" || s === "02" || s.includes("plazo") || s.includes("extension") || s.includes("extensión") || s.includes("renovac")) return "02";
    return s;
  };

  const formik = useFormik({
    initialValues: {
      fecha_inicio_cambio: solicitud?.fecha_inicio_cambio || "",
      cod_sap_nuevo_cargo: solicitud?.cod_sap_nuevo_cargo || "",
      nombre_nuevo_cargo: solicitud?.nombre_nuevo_cargo || "",
      nueva_unidad_organizativa: solicitud?.nueva_unidad_organizativa || "",
      nuevo_plan_horario: solicitud?.nuevo_plan_horario || "",
      descripcion_unidad_temporis: solicitud?.descripcion_unidad_temporis || "",
      unidad_destino: solicitud?.unidad_destino || "",
      tipo_cambio_contrato: normalizeTipoCambio(solicitud?.tipo_cambio_contrato),
      fecha_renovacion_contrato: solicitud?.fecha_renovacion_contrato || "",
      motivo_desvinculacion: solicitud?.motivo_desvinculacion || "",
      id_clausula_despido: solicitud?.id_clausula_despido || "",
      observacion_edicion: "",
    },
    enableReinitialize: true,
    validate,
    onSubmit: submitForm,
  });

  return {
    formik,
    posicionesLaborales,
    cargando,
  };
};
